import { Link } from "react-router-dom";
import { ARTICLES, localizeArticle } from "@/features/knowledge/articles";
import { useLocale, useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";

/** Nieuwste kennisbank-artikelen (max 3) — de rest staat in de kennisbank zelf. */
const ITEMS = ARTICLES.slice(0, 3);

/** Homepage-rij met kennisbank-/blogartikelen + een navy CTA-tegel naar de kennisbank. */
export function KennisbankRow() {
  const t = useT();
  const [locale] = useLocale();
  return (
    <section aria-labelledby="kennisbank-rij" className="mx-auto max-w-[1600px] px-4 pt-10 sm:pt-14">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h2 id="kennisbank-rij" className="text-lg font-bold text-primary-900 sm:text-2xl">{t("Uit de kennisbank")}</h2>
          <p className="mt-1 hidden text-sm text-primary-600 sm:block">{t("Opmeten, kiezen en vergelijken — handige uitleg en tips over glas op maat.")}</p>
        </div>
        <Link to="/kennisbank" className="shrink-0 text-sm font-semibold text-accent-700 hover:text-accent-700">
          {t("Alle artikelen")} →
        </Link>
      </div>

      {/* Mobiel (Coolblue-stijl): 1 grote kaart + 2 compacte rijen met foto links, tekst rechts. */}
      <div className="mt-4 grid gap-3 sm:mt-6 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {ITEMS.map((raw, i) => {
          const a = localizeArticle(raw, locale);
          const compact = i > 0; // mobiel: alleen de eerste als grote kaart
          return (
          <Link
            key={a.slug}
            to={`/kennisbank/${a.slug}`}
            className={`group border border-primary-100 bg-white transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md ${
              compact ? "flex flex-row items-stretch sm:flex-col" : "flex flex-col"
            }`}
          >
            <div className={`overflow-hidden bg-primary-50 ${compact ? "w-28 shrink-0 sm:aspect-[16/10] sm:w-full" : "aspect-[16/10] w-full"}`}>
              {/* De per artikel geschreven imageAlt (staat al in knowledge-index.json en wordt op
                  de artikelpagina zelf ook gebruikt); anders de titel. Stond hier leeg, waardoor
                  deze illustraties voor schermlezers en Google Afbeeldingen onzichtbaar waren. */}
              {a.image && <img src={cdnImage(a.image, { w: IMG.card })} alt={a.imageAlt ?? a.title} width={IMG.card} height={Math.round(IMG.card * 10 / 16)} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
            </div>
            <div className={`flex min-w-0 flex-1 flex-col ${compact ? "p-3.5 sm:p-6" : "p-4 sm:p-6"}`}>
              <span className="hidden w-max bg-primary-50 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary-700 sm:inline-block">{t(a.category)}</span>
              <h3 className={`font-bold leading-snug text-primary-900 transition group-hover:text-primary-700 ${compact ? "line-clamp-2 text-sm sm:mt-3 sm:text-lg" : "text-base sm:mt-3 sm:text-lg"}`}>{a.title}</h3>
              <p className={`mt-2 text-sm text-primary-600 ${compact ? "hidden sm:line-clamp-3 sm:block" : "line-clamp-2 sm:line-clamp-3"}`}>{a.description}</p>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-accent-700 sm:mt-4">{t("Lees meer")} →</span>
            </div>
          </Link>
          );
        })}
      </div>
    </section>
  );
}
